/** Uç nokta fonksiyonları: ekranlar fetch'i doğrudan çağırmaz. */
import { ApiError, NetworkError } from '../lib/errors';
import { api } from './client';
import type {
  Candidate, CancellationRow, CategoryRow, CreditRow, Settlement, CategorySuggestion, Dashboard, DuplicateCase, ExpenseCategoryDetail, ExpenseStats,
  Invitation, Me, Member, NotificationRow, Period, PersonSummary, Receipt, ReceiptDetail, RefundRow, ReportJob, SessionRow,
  SetupCode,
} from './types';

// --- kimlik
export const login = (username: string, password: string) =>
  api<Me>('/auth/login', { method: 'POST', body: { username, password, kind: 'web' } });
export const logout = () => api<void>('/auth/logout', { method: 'POST' });
export const me = () => api<Me>('/auth/me');
export const acceptInvite = (body: { token: string; username: string; full_name: string; password: string }) =>
  api<{ id: string; username: string }>('/auth/accept', { method: 'POST', body });
export const requestPasswordReset = (identifier: string) =>
  api<{ message: string }>('/auth/password-reset', { method: 'POST', body: { identifier } });
export const confirmPasswordReset = (token: string, new_password: string) =>
  api<void>('/auth/password-reset/confirm', { method: 'POST', body: { token, new_password } });
export const changePassword = (current_password: string, new_password: string) =>
  api<void>('/auth/password-change', { method: 'POST', body: { current_password, new_password } });
export const sessions = () => api<SessionRow[]>('/auth/sessions');
export const revokeOtherSessions = () => api<void>('/auth/sessions', { method: 'DELETE' });
export const members = () => api<Member[]>('/auth/members');
export const invitations = () => api<Invitation[]>('/auth/invites');
export const invite = (email: string, role: 'member' | 'admin') =>
  api<Invitation>('/auth/invites', { method: 'POST', body: { email, role } });
export const revokeInvite = (id: string) => api<void>(`/auth/invites/${id}/revoke`, { method: 'POST' });
export const deactivateMember = (user_id: string) => api<void>('/auth/members/deactivate', { method: 'POST', body: { user_id } });
export const reactivateMember = (user_id: string) => api<void>('/auth/members/reactivate', { method: 'POST', body: { user_id } });
/** İlk giriş / yönetici sıfırlaması sonrası: e-posta + 6 haneli kod + yeni şifre → oturum açılır. */
export const firstLogin = (email: string, code: string, new_password: string) =>
  api<Me>('/auth/first-login', { method: 'POST', body: { email, code, new_password } });
export const createMember = (body: { email: string; full_name: string; role: 'member' | 'admin' }) =>
  api<SetupCode>('/auth/members', { method: 'POST', body });
export const resetMemberPassword = (user_id: string) => api<SetupCode>(`/auth/members/${user_id}/reset-password`, { method: 'POST', body: {} });
export const setMemberRole = (user_id: string, role: 'member' | 'admin') =>
  api<void>(`/auth/members/${user_id}/role`, { method: 'POST', body: { role } });

// --- aday (taslak)
export const createCandidate = (client_draft_id: string, fields: Record<string, unknown>, idempotencyKey: string) =>
  api<Candidate>('/candidates', { method: 'POST', body: { client_draft_id, ...fields }, idempotencyKey });
export const getCandidate = (id: string) => api<Candidate>(`/candidates/${id}`);
export const updateCandidate = (id: string, expected_version: number, fields: Record<string, unknown>) =>
  api<Candidate>(`/candidates/${id}`, { method: 'PATCH', body: { expected_version, ...fields } });
export const deleteCandidate = (id: string) => api<void>(`/candidates/${id}`, { method: 'DELETE' });
export const uploadFile = (id: string, file: Blob, name: string, sha256: string, role: 'ORIGINAL' | 'PROOF' = 'ORIGINAL') => {
  const form = new FormData();
  form.append('file', file, name);
  form.append('sha256', sha256);
  form.append('role', role);
  return api<{ id: string }>(`/candidates/${id}/files`, { method: 'POST', form });
};
export const rerunOcr = (id: string) => api<void>(`/candidates/${id}/ocr`, { method: 'POST' });
export const submitCandidate = (id: string, expected_version: number, idempotencyKey: string) =>
  api<ReceiptDetail>(`/candidates/${id}/submit`, { method: 'POST', body: { expected_version, confirm_paid_by_me: true }, idempotencyKey });

// --- fiş
export interface ReceiptFilters { period_id?: string; owner_id?: string; mine?: '1'; category?: string; status?: string; q?: string; date_from?: string; date_to?: string }
export const receipts = (f: ReceiptFilters = {}) => api<Receipt[]>('/receipts', { query: { ...f } });
export const receipt = (id: string) => api<ReceiptDetail>(`/receipts/${id}`);
export const reviseReceipt = (id: string, expected_revision: number, reason: string, fields: Record<string, unknown>) =>
  api<ReceiptDetail>(`/receipts/${id}/revisions`, { method: 'POST', body: { expected_revision, reason, ...fields } });
export const cancelReceipt = (id: string, reason: string) => api<{ cancellation_id: string }>(`/receipts/${id}/cancel`, { method: 'POST', body: { reason } });
export const refundReceipt = (id: string, body: { amount: string; refund_date: string; reason: string }) =>
  api<{ refund_id: string; period_id: string }>(`/receipts/${id}/refunds`, { method: 'POST', body });
export const categorySuggest = (q: string, vendor = '') =>
  api<CategorySuggestion[]>('/categories/suggest', { query: { q, vendor: vendor || undefined } });
export const categories = () => api<CategoryRow[]>('/categories');
export const mergeCategory = (id: string, into_id: string) => api<void>(`/categories/${id}/merge`, { method: 'POST', body: { into_id } });
export const unmergeCategory = (id: string) => api<void>(`/categories/${id}/unmerge`, { method: 'POST', body: {} });
export const renameCategory = (id: string, name: string) => api<void>(`/categories/${id}/rename`, { method: 'POST', body: { name } });
export const fileUrl = (id: string) => api<{ url: string; expires_in: number }>(`/files/${id}/url`);
export const refunds = (period_id?: string) => api<RefundRow[]>('/refunds', { query: { period_id } });
export const cancellations = (period_id?: string) => api<CancellationRow[]>('/cancellations', { query: { period_id } });

// --- mükerrer inceleme (yönetici)
export const duplicateCases = () => api<DuplicateCase[]>('/duplicate-cases');
export const decideDuplicate = (id: string, decision: DuplicateCase['decision'], reason: string, candidate_version: number) =>
  api<DuplicateCase>(`/duplicate-cases/${id}/decision`, { method: 'POST', body: { decision, reason, candidate_version } });

// --- dönem, özet, rapor
export const periods = () => api<Period[]>('/periods');
export const period = (id: string) => api<Period>(`/periods/${id}`);
export const closePeriod = (id: string) => api<{ snapshot_id: string; content_hash: string }>(`/periods/${id}/close`, { method: 'POST', body: {} });
export const dashboard = (period_id?: string) => api<Dashboard>('/dashboard', { query: { period_id } });
export const personSummary = (user_id: string, period_id?: string) => api<PersonSummary>(`/people/${user_id}/summary`, { query: { period_id } });
export const reports = () => api<ReportJob[]>('/reports');
export const report = (id: string) => api<ReportJob>(`/reports/${id}`);
export const createReport = (body: { period_id: string; scope: 'SELF' | 'PERSON' | 'ALL'; owner_id?: string; include_photos: boolean }) =>
  api<ReportJob>('/reports', { method: 'POST', body });
export const notifications = () => api<NotificationRow[]>('/notifications');
export const readNotification = (id: string) => api<void>(`/notifications/${id}/read`, { method: 'POST' });
export const readAllNotifications = () => api<void>('/notifications/read-all', { method: 'POST' });

// --- telefon bildirimleri (Web Push)
export const pushKey = () => api<{ public_key: string }>('/push/key');
export const pushSubscribe = (sub: PushSubscriptionJSON) => api<{ subscribed: boolean }>('/push/subscriptions', { method: 'POST', body: sub });
export const pushUnsubscribe = (endpoint: string) => api<void>('/push/subscriptions', { method: 'DELETE', body: { endpoint } });
export const pushTest = () => api<{ devices: number }>('/push/test', { method: 'POST', body: {} });

// --- vadeli (veresiye) ödemeler
export const creditPayments = (state: 'open' | 'paid' | 'all' = 'open') => api<CreditRow[]>('/payments/credit', { query: { state } });
export const settleCredit = (id: string, paid_on: string, note?: string) =>
  api<Settlement>(`/receipts/${id}/settle`, { method: 'POST', body: { paid_on, note: note || null } });
export const unsettleCredit = (id: string) => api<void>(`/receipts/${id}/settle`, { method: 'DELETE' });

// --- gider istatistiği
export type StatsScope = { year_month: string } | { year: string };
export const expenseStats = (scope: StatsScope) => api<ExpenseStats>('/stats/expenses', { query: { ...scope } });
export const expenseCategory = (key: string, scope: StatsScope) =>
  api<ExpenseCategoryDetail>('/stats/expenses/category', { query: { key, ...scope } });

/** Ayın ya da yılın gider raporu PDF'i (sunucuda üretilir) — dosya olarak indirilir. */
export async function downloadStatsPdf(scope: StatsScope): Promise<void> {
  const url = new URL('/api/v1/stats/expenses/pdf', window.location.origin);
  for (const [k, v] of Object.entries(scope)) url.searchParams.set(k, v);
  let res: Response;
  try {
    res = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/pdf, application/json' } });
  } catch {
    throw new NetworkError();
  }
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { code?: string; message?: string; details?: Record<string, unknown> };
    throw new ApiError(err.code ?? `HTTP_${res.status}`, err.message ?? 'PDF oluşturulamadı.', res.status, err.details ?? {});
  }
  const blob = await res.blob();
  const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1]
    ?? `aksan-gider-${'year_month' in scope ? scope.year_month : scope.year}.pdf`;
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 60_000);
}
